// BlogPage.jsx - Public blog listing page
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Calendar, Clock, ChevronRight, Search, Tag } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '';

export default function BlogPage() {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    fetchArticles();
    fetchCategories();
  }, [selectedCategory, debouncedSearch]);

  const fetchArticles = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      if (debouncedSearch) params.append('search', debouncedSearch);
      params.append('limit', '20');

      const response = await fetch(`${API_BASE}/articles/public?${params}`);
      const data = await response.json();
      if (data.ok) {
        setArticles(data.articles);
      }
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE}/articles/categories`);
      const data = await response.json();
      if (data.ok) {
        setCategories(data.categories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const estimateReadTime = (content) => {
    const wordsPerMinute = 200;
    const words = content?.split(/\s+/).length || 0;
    const minutes = Math.ceil(words / wordsPerMinute);
    return `${minutes} min read`;
  };

  return (
    <>
      <Helmet>
        <title>Medicare Retention Blog | Pro IRP</title>
        <meta
          name="description"
          content="Expert insights on Medicare client retention, AEP strategies, compliance, and best practices for insurance agents and agencies."
        />
        <link rel="canonical" href="https://proirp.com/blog" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Hero Section - Enhanced with brand colors */}
        <div className="bg-gradient-to-br from-[#172A3A] via-[#1e3a50] to-[#172A3A] text-white relative overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#FFB800]/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl" />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 relative">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FFB800]/20 rounded-full text-[#FFB800] text-sm font-medium mb-6">
              <span className="w-2 h-2 bg-[#FFB800] rounded-full" />
              Expert Insights for Medicare Agents
            </div>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
              Medicare Retention<br />
              <span className="text-[#FFB800]">Insights & Strategies</span>
            </h1>
            <p className="text-xl text-blue-100/80 max-w-2xl leading-relaxed">
              Expert strategies, industry analysis, and best practices for Medicare agents
              focused on building and protecting their book of business.
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Search and Filter Bar - Enhanced */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-10 -mt-8 relative z-10">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#FFB800] focus:border-[#FFB800] transition-all bg-gray-50 focus:bg-white"
                />
              </div>

              {/* Category Filter */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedCategory(null)}
                  className={`px-5 py-3 rounded-xl text-sm font-semibold transition-all ${
                    !selectedCategory
                      ? 'bg-[#172A3A] text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  All Topics
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat.slug}
                    onClick={() => setSelectedCategory(cat.slug)}
                    className={`px-5 py-3 rounded-xl text-sm font-semibold transition-all ${
                      selectedCategory === cat.slug
                        ? 'bg-[#FFB800] text-[#172A3A] shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Articles Grid */}
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden animate-pulse">
                  <div className="h-48 bg-gray-200" />
                  <div className="p-6">
                    <div className="h-4 bg-gray-200 rounded w-1/4 mb-4" />
                    <div className="h-6 bg-gray-200 rounded w-3/4 mb-2" />
                    <div className="h-4 bg-gray-200 rounded w-full mb-4" />
                    <div className="h-4 bg-gray-200 rounded w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : articles.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-gray-500 text-lg">No articles found.</p>
              <p className="text-gray-400 mt-2">Check back soon for new content!</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {articles.map((article) => (
                <Link
                  key={article.id}
                  to={`/blog/${article.slug}`}
                  className="group bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden hover:shadow-xl hover:border-[#FFB800]/30 transition-all duration-300"
                >
                  {/* Featured Image */}
                  {article.featured_image ? (
                    <div className="h-52 overflow-hidden">
                      <img
                        src={article.featured_image}
                        alt={article.featured_image_alt || article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  ) : (
                    <div className="h-52 bg-gradient-to-br from-[#172A3A] to-[#1e3a50] flex items-center justify-center relative overflow-hidden">
                      {/* Decorative grid pattern */}
                      <div className="absolute inset-0 opacity-10" style={{
                        backgroundImage: 'radial-gradient(circle, rgba(255,184,0,0.3) 1px, transparent 1px)',
                        backgroundSize: '20px 20px'
                      }} />
                      <span className="text-white text-6xl font-bold opacity-20">
                        {article.title.charAt(0)}
                      </span>
                      <div className="absolute bottom-4 left-4 right-4">
                        <div className="w-16 h-1 bg-[#FFB800] rounded-full" />
                      </div>
                    </div>
                  )}

                  <div className="p-6">
                    {/* Category Tag */}
                    {article.category && (
                      <div className="flex items-center gap-1.5 text-sm text-[#FFB800] font-semibold mb-3">
                        <Tag className="w-3.5 h-3.5" />
                        {categories.find(c => c.slug === article.category)?.name || article.category}
                      </div>
                    )}

                    {/* Title */}
                    <h2 className="text-xl font-bold text-[#172A3A] mb-3 group-hover:text-[#1e3a50] transition-colors line-clamp-2 leading-snug">
                      {article.title}
                    </h2>

                    {/* Excerpt */}
                    <p className="text-gray-500 text-sm mb-5 line-clamp-3 leading-relaxed">
                      {article.excerpt}
                    </p>

                    {/* Meta */}
                    <div className="flex items-center justify-between text-sm pt-4 border-t border-gray-100">
                      <span className="flex items-center gap-2 text-gray-400">
                        <Calendar className="w-4 h-4" />
                        {formatDate(article.published_at)}
                      </span>
                      <span className="flex items-center gap-1 text-[#172A3A] font-semibold group-hover:text-[#FFB800] group-hover:gap-2 transition-all">
                        Read <ChevronRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {/* Newsletter Signup - Enhanced */}
          <div className="mt-20 bg-gradient-to-br from-[#172A3A] via-[#1e3a50] to-[#172A3A] rounded-3xl p-10 lg:p-16 text-white relative overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-80 h-80 bg-[#FFB800]/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl" />

            <div className="max-w-2xl mx-auto text-center relative">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FFB800]/20 rounded-full text-[#FFB800] text-sm font-medium mb-6">
                <span className="w-2 h-2 bg-[#FFB800] rounded-full" />
                Free Weekly Newsletter
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Stay Ahead of the Curve
              </h2>
              <p className="text-blue-100/80 mb-8 text-lg">
                Get the latest retention strategies, industry insights, and best practices
                delivered to your inbox every week.
              </p>
              <form className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto" onSubmit={async (e) => {
                e.preventDefault();
                const email = e.target.querySelector('input[type="email"]').value;
                if (!email) return;
                const btn = e.target.querySelector('button[type="submit"]');
                const origText = btn.textContent;
                btn.disabled = true;
                btn.textContent = 'Subscribing...';
                try {
                  const res = await fetch(`${API_BASE}/articles/subscribe`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email })
                  });
                  const data = await res.json();
                  if (data.ok) {
                    btn.textContent = 'Subscribed!';
                    e.target.querySelector('input[type="email"]').value = '';
                  } else {
                    btn.textContent = data.error || 'Failed';
                  }
                } catch {
                  btn.textContent = 'Error - try again';
                }
                setTimeout(() => { btn.disabled = false; btn.textContent = origText; }, 3000);
              }}>
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-1 px-5 py-4 rounded-xl text-gray-900 focus:ring-2 focus:ring-[#FFB800] focus:outline-none text-lg"
                  required
                />
                <button
                  type="submit"
                  className="px-8 py-4 bg-[#FFB800] text-[#172A3A] font-bold rounded-xl hover:bg-[#e5a600] transition-all hover:scale-105 shadow-lg shadow-[#FFB800]/25"
                >
                  Subscribe
                </button>
              </form>
              <p className="text-sm text-blue-200/50 mt-6">
                Join 2,000+ Medicare agents • No spam, unsubscribe anytime
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
